import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useUser } from '@/contexts/UserContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Trophy, TrendingUp, Users } from 'lucide-react';

// Talent-side preferences. Mirror of talents.preferences JSONB column.
// Real agent linking lives in the Profile page (LinkAgentModal), not here —
// this tab is just for behavior preferences.
interface TalentPreferences {
  performance_tracking: boolean;
  public_stats: boolean;
  auto_achievements: boolean;
  preferred_sports: string[];
  performance_frequency: 'daily' | 'weekly' | 'monthly';
  agent_linking: boolean;
}

const DEFAULT_PREFS: TalentPreferences = {
  performance_tracking: true,
  public_stats: true,
  auto_achievements: true,
  preferred_sports: ['basketball'],
  performance_frequency: 'weekly',
  agent_linking: true,
};

export const TalentSettings = () => {
  const { currentUser } = useUser();
  const { toast } = useToast();
  const [settings, setSettings] = useState<TalentPreferences>(DEFAULT_PREFS);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const loadPreferences = async () => {
    if (!currentUser?.id) return;
    try {
      // talents is keyed by profile_id, not user_id. currentUser.id is the
      // profile row id, so that's what we filter on.
      const { data, error } = await supabase
        .from('talents')
        .select('preferences')
        .eq('profile_id', currentUser.id)
        .maybeSingle();

      if (error) throw error;

      const prefs = data?.preferences as Partial<TalentPreferences> | null | undefined;
      if (prefs && typeof prefs === 'object' && !Array.isArray(prefs)) {
        setSettings({ ...DEFAULT_PREFS, ...prefs });
      }
    } catch (error) {
      console.error('Error loading talent preferences:', error);
    }
  };

  const updateSetting = useCallback(<K extends keyof TalentPreferences>(
    key: K,
    value: TalentPreferences[K],
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const saveSettings = async () => {
    if (!currentUser?.id) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('talents')
        .update({ preferences: settings })
        .eq('profile_id', currentUser.id);

      if (error) throw error;

      toast({
        title: 'Saved',
        description: 'Your athlete preferences have been updated.',
      });
    } catch (error) {
      console.error('Error saving talent preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to save athlete preferences. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Performance Tracking
          </CardTitle>
          <CardDescription>
            Manage how your athletic performance is tracked and displayed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="performance-tracking">Enable performance tracking</Label>
            <Switch
              id="performance-tracking"
              checked={settings.performance_tracking}
              onCheckedChange={(checked) => updateSetting('performance_tracking', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="public-stats">Make stats public</Label>
            <Switch
              id="public-stats"
              checked={settings.public_stats}
              onCheckedChange={(checked) => updateSetting('public_stats', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="auto-achievements">Auto-detect achievements</Label>
            <Switch
              id="auto-achievements"
              checked={settings.auto_achievements}
              onCheckedChange={(checked) => updateSetting('auto_achievements', checked)}
            />
          </div>

          <div className="space-y-2">
            <Label>Performance update frequency</Label>
            <Select
              value={settings.performance_frequency}
              onValueChange={(value) => updateSetting('performance_frequency', value as TalentPreferences['performance_frequency'])}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Agent Management
          </CardTitle>
          <CardDescription>
            Allow agents to send link requests. Use the Profile page to actually
            connect with an agent — that's where the linking flow lives.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="agent-linking">Allow agent linking</Label>
            <Switch
              id="agent-linking"
              checked={settings.agent_linking}
              onCheckedChange={(checked) => updateSetting('agent_linking', checked)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Sports & Categories
          </CardTitle>
          <CardDescription>
            Manage your sport categories and specializations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Primary Sport</Label>
            <Select
              value={settings.preferred_sports[0]}
              onValueChange={(value) => updateSetting('preferred_sports', [value])}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="basketball">Basketball</SelectItem>
                <SelectItem value="football">Football</SelectItem>
                <SelectItem value="soccer">Soccer</SelectItem>
                <SelectItem value="tennis">Tennis</SelectItem>
                <SelectItem value="baseball">Baseball</SelectItem>
                <SelectItem value="golf">Golf</SelectItem>
                <SelectItem value="swimming">Swimming</SelectItem>
                <SelectItem value="track">Track & Field</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Button onClick={saveSettings} disabled={isLoading} className="w-full">
        {isLoading ? 'Saving...' : 'Save Athlete Settings'}
      </Button>
    </div>
  );
};
